import { useContext, useMemo } from 'react'
import {
  NavigationContext,
  NavigationContextValue,
} from '../../context/NavigationContext'
import { useHistory } from 'react-router-dom'

/**
 * This hook utilizes the NavigationContext where relevant, allowing
 * navigation components to animate transitions between pages.
 * For example, if you use this component to navigate to a `NavigationPage`,
 * the page will slide in from the right. If you then use this hook to `goBack`,
 * the page will slide out to the right.
 *
 * Use this hook in place of useHistory() for navigation purposes.
 * @returns {object} An object with `goBack`, `goBackWithParam`, and `push` methods.
 * @example
 * const { goBack, goBackWithParam, push } = useNavigation()
 * const onClick = () => push('/some-page')
 * const onBackClick = () => goBack()
 * const onBackWithAlert = () => goBackWithParam('alert=true')
 */
export function useNavigation() {
  const history = useHistory()
  const navigationContext = useContext(NavigationContext)

  // useMemo to prevent rerenders if goBack or push
  // are passed as dependencies
  const navigation = useMemo(() => {
    const pushAddParam = (key: string, value: string) => {
      pushAddParamToHistory(history, key, value)
    }
    const pushRemoveParam = (key: string) => {
      pushRemoveParamFromHistory(history, key)
    }
    const pushRetainingParams = (
      path: string,
      additionalParams: Record<string, string> = {},
    ) => {
      pushWithRetainedParams(history, path, additionalParams)
    }
    const pushToPath = (path: string) => {
      pushOnToPath(history, navigationContext, path)
    }
    const goBackWithParam = (param: string) => {
      goBackWithParamToHistory(history, navigationContext, param)
    }

    if (navigationContext) {
      return {
        goBack: navigationContext.goBack,
        go: navigationContext.go,
        push: navigationContext.push,
        goHome: navigationContext.goHome,
        pushAddParam,
        pushRemoveParam,
        pushRetainingParams,
        pushToPath,
        goBackWithParam,
      }
    }

    return {
      goBack: history.goBack,
      go: history.go,
      push: history.push,
      goHome: () => history.push('/app'),
      pushAddParam,
      pushRemoveParam,
      pushRetainingParams,
      pushToPath,
      goBackWithParam,
    }
  }, [history, navigationContext])

  return navigation
}

function pushAddParamToHistory(
  history: ReturnType<typeof useHistory>,
  key: string,
  value: string,
) {
  const searchParams = new URLSearchParams(history.location.search)
  searchParams.set(key, value)
  history.push({ search: searchParams.toString() })
}

function pushRemoveParamFromHistory(
  history: ReturnType<typeof useHistory>,
  key: string,
) {
  const searchParams = new URLSearchParams(history.location.search)
  searchParams.delete(key)
  history.push({ search: searchParams.toString() })
}

function pushWithRetainedParams(
  history: ReturnType<typeof useHistory>,
  path: string,
  additionalParams: Record<string, string> = {},
) {
  // Split the path into pathname and search parts
  const [pathname, newSearchString] = path.split('?')

  // Get current search params from history
  const currentSearchParams = new URLSearchParams(history.location.search)

  // Parse any search params in the new path
  const newSearchParams = new URLSearchParams(newSearchString || '')

  // Merge params: new path params take precedence over current ones
  for (const [key, value] of newSearchParams.entries()) {
    currentSearchParams.set(key, value)
  }

  // Add the additional params (highest precedence)
  Object.entries(additionalParams).forEach(([key, value]) => {
    currentSearchParams.set(key, value)
  })

  // Push the new location
  history.push({
    pathname,
    search: currentSearchParams.toString(),
  })
}

function pushOnToPath(
  history: ReturnType<typeof useHistory>,
  navigationContext: NavigationContextValue | undefined,
  path: string,
) {
  const push = navigationContext?.push ?? history.push
  const existingPath = history.location.pathname
  const trailingSlash = existingPath.endsWith('/') ? '' : '/'
  const pathWithoutLeadingSlash = path.startsWith('/') ? path.slice(1) : path
  push(existingPath + trailingSlash + pathWithoutLeadingSlash)
}

function goBackWithParamToHistory(
  history: ReturnType<typeof useHistory>,
  navigationContext: NavigationContextValue | undefined,
  param: string,
) {
  // Create a listener to modify the URL after going back
  const unlisten = history.listen((location, action) => {
    if (action === 'POP') {
      // We've navigated back, now add/update the parameter
      unlisten() // Remove the listener immediately

      // Parse existing search params
      const searchParams = new URLSearchParams(location.search)

      // Parse the new parameter (e.g., "alert=true" -> key: "alert", value: "true")
      const [key, value] = param.split('=')
      if (key && value !== undefined) {
        searchParams.set(key, value) // This will override existing values or add new ones
      }

      // Replace the current location with the updated parameters
      history.replace({
        pathname: location.pathname,
        search: searchParams.toString(),
      })
    }
  })

  // Perform the back navigation
  if (navigationContext) {
    navigationContext.goBack()
  } else {
    history.goBack()
  }
}
